import { supabase } from "@/integrations/supabase/client";

interface EmailValidationResult {
  exists: boolean;
  userType?: 'seller' | 'buyer' | null;
  error: string | null;
}

/**
 * Email validation service for real-time email checking during registration
 */
export const emailValidationService = {
  /**
   * Check if an email address is already registered in the system
   * @param email - Email address to check
   * @returns Object with exists boolean and error message if any
   */
  checkEmailExists: async (email: string): Promise<EmailValidationResult> => {
    try {
      // Validate email format first
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email)) {
        return { exists: false, error: null };
      }

      // Call enhanced Supabase RPC function to check email and get user type
      const { data, error } = await supabase.rpc('check_email_with_type', {
        email_to_check: email.toLowerCase().trim()
      });

      if (error) {
        console.error('Email validation error:', error);
        return { exists: false, userType: null, error: 'Unable to verify email availability' };
      }

      // Data returns an array with one row: [{ email_exists: boolean, user_type: string }]
      if (!data || !Array.isArray(data) || data.length === 0) {
        return { exists: false, userType: null, error: null };
      }

      const result = data[0];
      
      return { 
        exists: result.email_exists === true, 
        userType: (result.user_type as 'seller' | 'buyer') || null,
        error: null 
      };
    } catch (error) {
      console.error('Email validation exception:', error);
      return { exists: false, error: 'Unable to verify email availability' };
    }
  }
};
